import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';

/**
 * My Garage — the seller's own listings, as shown in the topbar's
 * "My Transaction" panel.
 *
 * This has its own slice rather than reusing ManageListingsPage's: the topbar
 * renders on every page, so dispatching that page's query from here would
 * overwrite the list a seller is actually looking at whenever the panel is
 * opened on ManageListingsPage itself.
 *
 * Deliberately fetched on demand (when the panel opens) rather than on mount —
 * the topbar is on every route, and nobody should pay for this query until
 * they ask to see it.
 */

// A summary card only needs a handful of listings; "and N more" covers the rest.
export const GARAGE_PREVIEW_COUNT = 3;

// Exported so the query shape and normalisation can be tested directly —
// createAsyncThunk does not expose its payload creator.
export const fetchMyGaragePayloadCreator = async (arg, thunkAPI) => {
  const { extra: sdk, rejectWithValue } = thunkAPI;

  try {
    const response = await sdk.ownListings.query({
      page: 1,
      perPage: GARAGE_PREVIEW_COUNT,
      'fields.listing': ['title', 'state', 'publicData'],
    });

    const listings = response?.data?.data || [];
    // `meta.totalItems` is the real count across all pages; the array above is
    // only the preview slice, so counting it would under-report the garage.
    const totalItems = response?.data?.meta?.totalItems ?? listings.length;

    return {
      listings: listings.map(l => ({
        id: l.id?.uuid || null,
        title: l.attributes?.title || '',
        state: l.attributes?.state || null,
      })),
      totalItems,
    };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const fetchMyGarageThunk = createAsyncThunk(
  'app/myGarage/fetchMyGarage',
  fetchMyGaragePayloadCreator
);

export const fetchMyGarage = () => dispatch => dispatch(fetchMyGarageThunk());

const initialState = {
  listings: [],
  totalItems: 0,
  fetchInProgress: false,
  fetchError: null,
  loaded: false,
};

const myGarageSlice = createSlice({
  name: 'app/myGarage',
  initialState,
  reducers: {
    clearMyGarage: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(fetchMyGarageThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchMyGarageThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.listings = action.payload.listings;
        state.totalItems = action.payload.totalItems;
        state.loaded = true;
      })
      .addCase(fetchMyGarageThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
        // `loaded` stays false so opening the panel again retries rather than
        // showing an empty garage as though the seller has no listings.
        state.listings = [];
        state.totalItems = 0;
      });
  },
});

export const { clearMyGarage } = myGarageSlice.actions;
export default myGarageSlice.reducer;
